/**
 * Landing pages: city and industry pages written as markdown, in the same
 * format as blog posts (see blogFormat.ts) plus a few extra frontmatter keys.
 *
 * Like the blog, this module is pure, so the app and scripts/prerender.mjs
 * render a page, its JSON-LD and its FAQ schema from the same functions.
 *
 * Kinds and their URLs:
 *   city-web   /city-<slug>                      web design in a city
 *   city-ads   /google-ads-management/<slug>     Google Ads management in a city
 *   city-ai    /ai-automation/<slug>             AI workflow automation in a city
 *   industry   /industries/<slug>                what we do for one industry
 *
 * Every page must say something specific to its city or industry; the
 * content agents were told to use sourced local facts. Pages that only swap
 * a place name are doorway pages under Google's spam policies.
 */
import { parsePost, renderMarkdown, escapeHtml, SITE_ORIGIN, type BlogPostData, type BlogFaq } from './blogFormat';

export type LandingKind = 'city-web' | 'city-ads' | 'city-ai' | 'industry';

export interface LandingPageData extends BlogPostData {
  kind: LandingKind;
  /** Visible H1; defaults to title. */
  h1?: string;
  /** One-sentence subtitle under the H1. */
  subtitle?: string;
  city?: string;
  /** Full state name, e.g. Texas. */
  state?: string;
  industry?: string;
}

const KINDS: LandingKind[] = ['city-web', 'city-ads', 'city-ai', 'industry'];

export function parseLanding(source: string, fileName = '?'): LandingPageData {
  const page = parsePost(source, fileName) as LandingPageData;
  if (!KINDS.includes(page.kind)) throw new Error(`landing ${fileName}: unknown kind "${page.kind}"`);
  if (page.kind !== 'industry' && (!page.city || !page.state)) {
    throw new Error(`landing ${fileName}: city pages need "city" and "state"`);
  }
  if (page.kind === 'industry' && !page.industry) throw new Error(`landing ${fileName}: industry pages need "industry"`);
  return page;
}

export const landingPath = (p: Pick<LandingPageData, 'kind' | 'slug'>) =>
  p.kind === 'city-web'
    ? `/city-${p.slug}`
    : p.kind === 'city-ads'
      ? `/google-ads-management/${p.slug}`
      : p.kind === 'city-ai'
        ? `/ai-automation/${p.slug}`
        : `/industries/${p.slug}`;

export const landingUrl = (p: Pick<LandingPageData, 'kind' | 'slug'>) => `${SITE_ORIGIN}${landingPath(p)}`;

/** The service each kind sells, for breadcrumbs, schema and the CTA. */
export const SERVICE: Record<LandingKind, { name: string; path: string; serviceType: string }> = {
  'city-web': { name: 'Web Design', path: '/software/webdesign', serviceType: 'Web design' },
  'city-ads': { name: 'Google Ads Management', path: '/google-ads-management', serviceType: 'Google Ads management' },
  'city-ai': { name: 'AI Workflow Automation', path: '/software/ai-workflow-automation', serviceType: 'AI workflow automation' },
  industry: { name: 'Industries', path: '/industries', serviceType: 'Web design, Google Ads and AI automation' },
};

const faqLd = (faq: BlogFaq[]) =>
  faq.length
    ? [{
        '@type': 'FAQPage',
        mainEntity: faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
      }]
    : [];

/** Service + BreadcrumbList + FAQPage, all derived from the visible page. */
export function landingJsonLd(p: LandingPageData) {
  const url = landingUrl(p);
  const svc = SERVICE[p.kind];
  const areaServed =
    p.kind === 'industry'
      ? { '@type': 'Country', name: 'United States' }
      : { '@type': 'City', name: p.city, containedInPlace: { '@type': 'State', name: p.state } };
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        name: p.h1 || p.title,
        serviceType: svc.serviceType,
        description: p.description,
        url,
        provider: { '@id': `${SITE_ORIGIN}/#organization` },
        areaServed,
        ...(p.industry && { audience: { '@type': 'BusinessAudience', audienceType: p.industry } }),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
          { '@type': 'ListItem', position: 2, name: svc.name, item: `${SITE_ORIGIN}${svc.path}` },
          { '@type': 'ListItem', position: 3, name: p.h1 || p.title, item: url },
        ],
      },
      ...faqLd(p.faq),
    ],
  };
}

/** The page's main content as static HTML; the prerenderer and React both use it. */
export function renderLanding(p: LandingPageData): string {
  const svc = SERVICE[p.kind];
  const takeaways = p.takeaways.length
    ? `<aside class="takeaways" aria-label="At a glance"><h2>At a glance</h2><ul>${p.takeaways
        .map((t) => `<li>${escapeHtml(t)}</li>`)
        .join('')}</ul></aside>`
    : '';
  const faq = p.faq.length
    ? `<section class="faq" aria-labelledby="faq"><h2 id="faq">Frequently asked questions</h2>${p.faq
        .map((f) => `<h3>${escapeHtml(f.q)}</h3><p>${escapeHtml(f.a)}</p>`)
        .join('')}</section>`
    : '';
  return [
    `<nav class="crumbs" aria-label="Breadcrumb"><a href="/">Home</a> / <a href="${svc.path}">${escapeHtml(svc.name)}</a> / <span>${escapeHtml(p.city || p.industry || '')}</span></nav>`,
    `<h1>${escapeHtml(p.h1 || p.title)}</h1>`,
    p.subtitle ? `<p class="subtitle">${escapeHtml(p.subtitle)}</p>` : '',
    takeaways,
    renderMarkdown(p.body),
    faq,
  ].filter(Boolean).join('\n');
}

const KIND_LABEL: Record<LandingKind, string> = {
  'city-web': 'Web design',
  'city-ads': 'Google Ads management',
  'city-ai': 'AI automation',
  industry: '',
};

export const landingLinkLabel = (p: LandingPageData) =>
  p.kind === 'industry' ? p.industry! : `${KIND_LABEL[p.kind]} in ${p.city}, ${p.state}`;

/**
 * Internal links for a page: its other services in the same city, then nearby
 * pages of the same kind (same state first). Pure, so the prerendered HTML and
 * the React page link to exactly the same URLs.
 */
export function relatedLandings(p: LandingPageData, all: LandingPageData[], max = 6): LandingPageData[] {
  // Industries have no place to be "near", so link the neighbors in the
  // alphabetical list (wrapping). Every industry is then linked from its
  // siblings, instead of all 30 pointing at the same first six.
  if (p.kind === 'industry') {
    const list = all.filter((o) => o.kind === 'industry').sort((a, b) => a.industry!.localeCompare(b.industry!));
    const i = list.indexOf(p);
    return [...list.slice(i + 1), ...list.slice(0, i)].slice(0, max);
  }
  const others = all.filter((o) => o !== p);
  const sameCity = p.city ? others.filter((o) => o.city === p.city && o.state === p.state) : [];
  const sameKind = others
    .filter((o) => o.kind === p.kind && !sameCity.includes(o))
    .sort((a, b) => Number(b.state === p.state) - Number(a.state === p.state) || landingLinkLabel(a).localeCompare(landingLinkLabel(b)));
  return [...sameCity, ...sameKind].slice(0, max);
}

export const renderRelated = (p: LandingPageData, all: LandingPageData[]) => {
  const rel = relatedLandings(p, all);
  return rel.length
    ? `<nav class="related" aria-label="Related pages"><h2>Related</h2><ul>${rel
        .map((r) => `<li><a href="${landingPath(r)}">${escapeHtml(landingLinkLabel(r))}</a></li>`)
        .join('')}</ul></nav>`
    : '';
};

/** Hub page bodies (/locations and /industries), shared with the prerenderer. */
export function renderHub(hub: 'locations' | 'industries', all: LandingPageData[]): string {
  const list = (items: LandingPageData[], label: (p: LandingPageData) => string) =>
    `<ul>${items.map((p) => `<li><a href="${landingPath(p)}">${escapeHtml(label(p))}</a></li>`).join('')}</ul>`;
  if (hub === 'industries') {
    const items = all.filter((p) => p.kind === 'industry');
    return `<h1>Industries we work with</h1><p>What we build and run for each kind of small business: the website, the Google Ads and the automations that fit how that industry actually gets and serves customers.</p>${list(items, (p) => p.industry!)}`;
  }
  const byCity = (kind: LandingKind) =>
    all.filter((p) => p.kind === kind).sort((a, b) => a.state!.localeCompare(b.state!) || a.city!.localeCompare(b.city!));
  const section = (kind: LandingKind, heading: string) => {
    const items = byCity(kind);
    return items.length ? `<h2>${heading}</h2>${list(items, (p) => `${p.city}, ${p.state}`)}` : '';
  };
  return [
    '<h1>Where we work</h1>',
    '<p>We work remotely with small businesses across the United States. These pages cover what we do in specific cities and what is different about each market.</p>',
    section('city-ads', 'Google Ads management'),
    section('city-ai', 'AI workflow automation'),
    section('city-web', 'Web design'),
  ].join('');
}
